import React, { useEffect, useState } from 'react';
import { Search, PenLine, SlidersHorizontal, Sparkles, ArrowLeft, Bell } from 'lucide-react';
import { ProjectOverview, getActionCounts } from './ProjectOverview';
import { PortfolioTab } from './PortfolioTab';
import { DocumentsTab } from './DocumentsTab';
import { GpDetailPage } from './GpDetailPage';
import { SourceAnnotation } from './SourceAnnotation';
interface Project {
  id: number;
  name: string;
  type: string;
  status: string;
  sellers: string;
  buyers: string;
  advisoryLead: string;
  sellerLegal: string;
  buyerLegal: string;
  lastUpdated?: string;
}
interface ProjectDetailPageProps {
  project: Project;
  onBack: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
}
const TABS = ['Overview', 'Portfolio', 'Documents'];
export function ProjectDetailPage({
  project,
  onBack,
  activeTab,
  onTabChange
}: ProjectDetailPageProps) {
  const [selectedGp, setSelectedGp] = useState<string | null>(null);
  const [selectedGpTab, setSelectedGpTab] = useState<string | undefined>(undefined);
  const [isActionsPanelOpen, setIsActionsPanelOpen] = useState(false);
  const actionCounts = getActionCounts(project.name);
  const handleGpSelect = (gp: string, tab?: string) => {
    setSelectedGp(gp);
    setSelectedGpTab(tab);
  };
  // Reset GP selection when project changes (e.g. sidebar nav)
  useEffect(() => {
    setSelectedGp(null);
    setSelectedGpTab(undefined);
  }, [project.id]);
  if (selectedGp) {
    return <div className="p-8 pb-32 max-w-[110rem] mx-auto bg-white min-h-screen" data-id="element-484">
        <GpDetailPage project={project} gpName={selectedGp} onBack={() => {
        setSelectedGp(null);
        setSelectedGpTab(undefined);
      }} onBackToProjects={onBack} initialTab={selectedGpTab} data-id="element-485" />
      </div>;
  }
  return <div className="p-8 pb-32 max-w-[110rem] mx-auto bg-white min-h-screen" data-id="element-486">
      {/* Back Button & Header */}
      <div className="mb-6" data-id="element-487">
        <button onClick={onBack} className="flex items-center text-gray-400 hover:text-gray-900 text-sm font-medium transition-colors mb-4" data-id="element-488">
          ← Projects
        </button>

        <div className="flex justify-between items-center" data-id="element-489">
          <div className="flex flex-col" data-id="element-490">
            <div className="flex items-center gap-3" data-id="element-491">
              <h1 className="text-4xl font-bold text-gray-900" data-id="element-492">
                {project.name}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${project.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`} data-id="element-493">
                {project.status}
              </span>
            </div>
            {project.lastUpdated && <span className="text-xs text-gray-400 mt-1" data-id="element-494">
                Updated {project.lastUpdated}
              </span>}
          </div>
          <div className="flex items-center gap-4" data-id="element-495">
            <div className="flex w-64 shadow-sm rounded-md overflow-hidden border border-gray-300" data-id="element-496">
              <input type="text" placeholder="Search project..." className="flex-1 px-4 py-2 text-sm outline-none" data-id="element-497" />
            </div>
            {/* Actions Bell Button */}
            <button onClick={() => setIsActionsPanelOpen(true)} className="relative p-2.5 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors" title="View actions" data-id="element-498">
              <Bell className="w-4 h-4" data-id="element-499" />
              {actionCounts.total > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[#458b85] text-white text-[10px] font-bold px-1" data-id="element-500">
                  {actionCounts.total}
                </span>}
            </button>
            <div className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-bold" data-id="element-501">
              JC
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center border-b border-gray-200 mb-8" data-id="element-502">
        {TABS.map(tab => <button key={tab} onClick={() => onTabChange(tab)} className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === tab ? 'border-[#458b85] text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-700'}`} data-id="element-503">
            {tab}
          </button>)}
      </div>

      {/* Tab Content */}
      <div data-id="element-504">
        {activeTab === 'Overview' ? <>
            {/* Metadata Bar — Overview tab only */}
            <div className="bg-white border border-gray-200 rounded-lg mb-8 shadow-sm overflow-hidden" data-id="element-505">
              <div className="grid grid-cols-3 border-b border-gray-100" data-id="element-506">
                <div className="p-5" data-id="element-507">
                  <span className="text-gray-400 text-[11px] font-bold uppercase tracking-wider block mb-2" data-id="element-508">
                    Seller
                  </span>
                  <span className="text-gray-900 text-sm font-medium" data-id="element-509">
                    <SourceAnnotation sourceType="document" sourceTitle="Purchase & Sale Agreement.pdf" sourceDate="Today" extractedValue={project.sellers} context="Seller Entity" data-id="element-510">
                      {project.sellers}
                    </SourceAnnotation>
                  </span>
                </div>
                <div className="p-5 border-l border-gray-100" data-id="element-511">
                  <span className="text-gray-400 text-[11px] font-bold uppercase tracking-wider block mb-2" data-id="element-512">
                    Buyer(s)
                  </span>
                  <span className="text-gray-900 text-sm font-medium" data-id="element-513">
                    <SourceAnnotation sourceType="document" sourceTitle="Purchase & Sale Agreement.pdf" sourceDate="Today" extractedValue={project.buyers} context="Buyer Entity" data-id="element-514">
                      {project.buyers}
                    </SourceAnnotation>
                  </span>
                </div>
                <div className="p-5 border-l border-gray-100" data-id="element-515">
                  <span className="text-gray-400 text-[11px] font-bold uppercase tracking-wider block mb-2" data-id="element-516">
                    Project Type
                  </span>
                  <span className="text-gray-900 text-sm font-medium" data-id="element-517">
                    <SourceAnnotation sourceType="document" sourceTitle="Purchase & Sale Agreement.pdf" sourceDate="Today" extractedValue={project.type} context="Project Type" data-id="element-518">
                      {project.type}
                    </SourceAnnotation>
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3" data-id="element-519">
                <div className="p-5" data-id="element-520">
                  <span className="text-gray-400 text-[11px] font-bold uppercase tracking-wider block mb-2" data-id="element-521">
                    Seller Legal
                  </span>
                  <span className="text-gray-900 text-sm font-medium" data-id="element-522">
                    <SourceAnnotation sourceType="document" sourceTitle="Engagement Letter.pdf" sourceDate="Today" extractedValue={project.sellerLegal} context="Seller Legal Counsel" data-id="element-523">
                      {project.sellerLegal}
                    </SourceAnnotation>
                  </span>
                </div>
                <div className="p-5 border-l border-gray-100" data-id="element-524">
                  <span className="text-gray-400 text-[11px] font-bold uppercase tracking-wider block mb-2" data-id="element-525">
                    Buyer Legal
                  </span>
                  <span className="text-gray-900 text-sm font-medium" data-id="element-526">
                    <SourceAnnotation sourceType="document" sourceTitle="Engagement Letter.pdf" sourceDate="Today" extractedValue={project.buyerLegal} context="Buyer Legal Counsel" data-id="element-527">
                      {project.buyerLegal}
                    </SourceAnnotation>
                  </span>
                </div>
                <div className="p-5 border-l border-gray-100" data-id="element-528">
                  <span className="text-gray-400 text-[11px] font-bold uppercase tracking-wider block mb-2" data-id="element-529">
                    Advisory Lead
                  </span>
                  <span className="text-gray-900 text-sm font-medium" data-id="element-530">
                    <SourceAnnotation sourceType="document" sourceTitle="Engagement Letter.pdf" sourceDate="Today" extractedValue={project.advisoryLead} context="Advisory Lead" data-id="element-531">
                      {project.advisoryLead}
                    </SourceAnnotation>
                  </span>
                </div>
              </div>
            </div>
            <ProjectOverview projectName={project.name} onGpSelect={handleGpSelect} isPanelOpen={isActionsPanelOpen} onPanelOpenChange={setIsActionsPanelOpen} data-id="element-532" />
          </> : activeTab === 'Portfolio' ? <PortfolioTab data-id="element-533" /> : activeTab === 'Documents' ? <DocumentsTab data-id="element-534" /> : <div className="bg-gray-50 rounded-xl p-12 text-center text-gray-500" data-id="element-535">
            <p className="text-base" data-id="element-536">
              Content for {activeTab} is not yet implemented.
            </p>
          </div>}
      </div>
    </div>;
}